import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Collapse,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { NavLeafItem } from './NavLeafItem.js';
import { groupSegmentPseudo, type LineMode, tickPseudo, wrapperLinePseudo } from './treeLines.js';
import type { NavGroup } from './types.js';

export function NavGroupItem({
  group,
  inPopover = false,
  showTopDivider = false,
  isLastInSection = false,
  onClose,
}: {
  group: NavGroup;
  inPopover?: boolean;
  showTopDivider?: boolean;
  isLastInSection?: boolean;
  onClose?: () => void;
}) {
  const lineMode: LineMode | undefined = inPopover
    ? undefined
    : isLastInSection
    ? 'segment-half'
    : 'segment-full';
  const { pathname } = useLocation();
  const isActive = group.isActive?.(pathname) ?? false;
  const [userOpen, setUserOpen] = useState<boolean | null>(null);
  const open = userOpen ?? isActive;

  // Reset manual toggle on navigation so the new active route auto-expands.
  useEffect(() => {
    setUserOpen(null);
  }, [pathname]);

  if (inPopover) {
    return (
      <>
        {showTopDivider && (
          <Divider
            sx={(theme) => ({
              borderColor: alpha(theme.palette.common.white, 0.08),
              mx: 1,
              my: 0.75,
            })}
          />
        )}
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            pt: 1,
            pb: 0.25,
            color: isActive ? theme.palette.secondary.main : alpha(theme.palette.common.white, 0.5),
          })}
        >
          {group.icon}
          <Typography
            sx={(theme) => ({
              fontWeight: 600,
              fontSize: '0.78rem',
              color: isActive
                ? theme.palette.secondary.main
                : alpha(theme.palette.common.white, 0.5),
            })}
            variant="body2"
          >
            {group.label}
          </Typography>
        </Box>
        {group.children.map((child) => (
          <NavLeafItem key={child.href} item={child} onClose={onClose} />
        ))}
      </>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        ...(lineMode && { '&::after': groupSegmentPseudo(lineMode) }),
      }}
    >
      <ListItemButton
        sx={(theme) => ({
          position: 'relative',
          minHeight: 36,
          py: 0.5,
          my: 0.25,
          pl: 1.25,
          pr: 2,
          borderRadius: 1,
          gap: 1,
          color: isActive ? theme.palette.secondary.main : alpha(theme.palette.common.white, 0.7),
          transition: 'none',
          ...(open &&
            !isActive && {
              bgcolor: alpha(theme.palette.common.white, 0.06),
              color: theme.palette.common.white,
            }),
          ...(open &&
            isActive && {
              bgcolor: alpha(theme.palette.secondary.main, 0.13),
              '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.2) },
            }),
          '&::before': tickPseudo(),
          '&:hover': {
            bgcolor: alpha(theme.palette.common.white, 0.06),
            color: theme.palette.common.white,
          },
        })}
        onClick={() => setUserOpen((v) => !(v ?? isActive))}
      >
        <ListItemIcon sx={{ minWidth: 0, color: 'inherit' }}>{group.icon}</ListItemIcon>
        <ListItemText
          primary={group.label}
          slotProps={{
            primary: { variant: 'body2', fontWeight: isActive ? 600 : 400, color: 'inherit' },
          }}
        />
        {open ? (
          <ExpandLessIcon sx={{ fontSize: 16, opacity: 0.6 }} />
        ) : (
          <ExpandMoreIcon sx={{ fontSize: 16, opacity: 0.6 }} />
        )}
      </ListItemButton>
      <Collapse unmountOnExit in={open} timeout="auto">
        <Box
          sx={{
            position: 'relative',
            ml: '10px',
            pl: '12px',
            mb: 0.5,
            '&::before': wrapperLinePseudo(),
          }}
        >
          <List dense disablePadding>
            {group.children.map((child) => (
              <NavLeafItem key={child.href} indented item={child} onClose={onClose} />
            ))}
          </List>
        </Box>
      </Collapse>
    </Box>
  );
}
